import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowUpRight, Download, FileText, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import SEOHead from '../components/common/SEOHead';
import NotFound from './NotFound';

const TIMELINE_OPTIONS = [
    { value: '0-3-months', label: 'Actively buying (0–3 months)' },
    { value: '3-6-months', label: 'Ready in 3–6 months' },
    { value: '6-12-months', label: 'Planning 6–12 months' },
    { value: '12-plus-months', label: '12+ months out' },
    { value: 'just-looking', label: 'Just researching' },
];

const AREAS = ['durham', 'hamilton', 'halton', 'niagara', 'brantford', 'brant-county'];

const SECTIONS = [
    'Executive summary', 'Housing market', 'Schools',
    'Transit', 'Walkability', 'Lifestyle',
    'Restaurants', 'Future development', 'Investment outlook',
    'Pros & cons', 'Demographics', 'Commute',
    'Parks & hidden gems', 'Photography', 'Charts & maps',
    'Neighbourhood score',
];

/**
 * Neighbourhood Intelligence Report — the lead-gated PDF download.
 * Editorial split layout: preview + credibility on the left, form on the right.
 * On submit, the CTA becomes a download button and the PDF streams.
 */
export default function NeighbourhoodReport() {
    const { slug } = useParams();
    const { data: n, isLoading, isError } = useQuery({
        queryKey: ['neighbourhood', slug],
        queryFn: async () => (await api.get(`/neighbourhoods/${slug}`)).data,
        retry: false,
    });

    const [f, setF] = useState({
        name: '', email: '', phone: '', timeline: '3-6-months', budget: '', area: '',
    });
    const [busy, setBusy] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState(null);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

    if (isLoading) return <div className="min-h-[60vh]" />;
    if (isError || !n) return <NotFound />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = { ...f, budget: Number(f.budget) };
            const { data } = await api.post(`/reports/neighbourhoods/${slug}/request`, payload);
            const backend = process.env.REACT_APP_BACKEND_URL;
            const full = `${backend}${data.download_url}`;
            setDownloadUrl(full);
            toast.success('Report ready — starting your download.');
            // Kick off browser download immediately
            window.location.href = full;
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === 'string' ? detail : 'Could not prepare the report.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="bg-white">
            <SEOHead
                title={`${n.name} · Intelligence Report — Free PDF`}
                description={`Executive summary, market data, schools, transit, investment outlook and more. A professional research report on ${n.name}, ${n.region}. Free with signup.`}
                path={`/neighbourhoods/${slug}/report`}
                image={n.hero_image}
            />

            {/* Hero */}
            <section className="relative bg-brand-navy text-white">
                <img src={n.hero_image} alt={n.name} className="absolute inset-0 w-full h-full object-cover opacity-45" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.55) 100%)' }} />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-16 lg:pb-20">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.24em] uppercase text-white/70 font-mono-data">
                        <span className="inline-block w-6 h-px bg-white/60" />
                        Intelligence Report · Southern Ontario
                    </div>
                    <h1
                        className="font-editorial mt-6 tracking-editorial max-w-4xl"
                        style={{ fontSize: 'clamp(38px, 5.8vw, 82px)', lineHeight: 1.03, letterSpacing: '-0.03em' }}
                        data-testid="report-title"
                    >
                        The {n.name} Intelligence Report
                    </h1>
                    <p className="mt-5 font-editorial italic text-white/85 max-w-2xl" style={{ fontSize: '19px', lineHeight: 1.5 }}>
                        A professional research report — {SECTIONS.length} sections, verified data, no listings.
                    </p>
                </div>
            </section>

            {/* Body */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* Left · preview */}
                <div className="lg:col-span-7 space-y-10">
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <div className="overline">What&apos;s inside</div>
                        <h2 className="font-editorial mt-3 leading-tight" style={{ fontSize: '32px', letterSpacing: '-0.02em' }}>
                            Sixteen sections. Investment-research aesthetic.
                        </h2>
                        <p className="mt-4 text-[15px] text-neutral-700 leading-relaxed max-w-2xl">
                            Prepared the same way we&apos;d prepare a private-client research brief — Bloomberg
                            density, editorial typography, sharp corners, and every number sourced.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-0 border-l border-t border-neutral-200">
                        {SECTIONS.map((s, i) => (
                            <div key={s} className="border-r border-b border-neutral-200 p-4">
                                <div className="font-mono-data text-[10px] tabular text-neutral-400">
                                    {String(i + 1).padStart(2, '0')}
                                </div>
                                <div className="mt-2 font-editorial text-[14.5px] leading-tight tracking-editorial">
                                    {s}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="border border-neutral-200 p-6 bg-[#FAFAF8]">
                        <div className="flex items-start gap-3">
                            <FileText className="w-5 h-5 mt-1" />
                            <div>
                                <div className="overline">Delivered as</div>
                                <p className="mt-2 text-[14.5px] text-neutral-800 leading-relaxed">
                                    A single PDF (typically 6-10 pages) generated on demand from live data.
                                    Photography, price-history chart, and a neighbourhood score visualisation
                                    are embedded — Bloomberg-density, editorial-lean.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right · form */}
                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-24 border border-neutral-200 bg-white p-6 lg:p-8" data-testid="report-form-card">
                        <div className="overline">Free · One-page signup</div>
                        <h3 className="font-editorial mt-3 leading-tight" style={{ fontSize: '24px', letterSpacing: '-0.01em' }}>
                            Download the {n.name} report.
                        </h3>
                        <p className="mt-3 text-[13px] text-neutral-600 leading-relaxed">
                            One PDF. Your details reach an independent analyst — never a listing agent, never a
                            call centre.
                        </p>

                        {downloadUrl ? (
                            <div className="mt-8 space-y-4">
                                <div className="flex items-start gap-3 border border-emerald-200 bg-emerald-50 p-4">
                                    <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-700" />
                                    <div className="text-[13.5px] text-emerald-900 leading-relaxed">
                                        Your download should have started. If not, use the button below.
                                    </div>
                                </div>
                                <a
                                    href={downloadUrl}
                                    data-testid="report-download-btn"
                                    className="inline-flex items-center justify-center gap-2 w-full bg-brand-navy text-white px-6 py-4 text-[14px] font-medium hover:bg-neutral-800"
                                    style={{ letterSpacing: '0.02em' }}
                                >
                                    <Download className="w-4 h-4" /> Download PDF again
                                </a>
                                <Link
                                    to="/strategy-session"
                                    className="inline-flex items-center justify-center gap-2 w-full border border-neutral-400 text-black px-6 py-4 text-[14px] hover:bg-brand-navy hover:text-white transition-colors"
                                   
                                    data-testid="report-strategy-cta"
                                >
                                    Book a strategy session <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="report-form">
                                <Input label="Full name" value={f.name} onChange={(v) => set('name', v)} required testid="report-name" />
                                <Input label="Email" value={f.email} onChange={(v) => set('email', v)} type="email" required testid="report-email" />
                                <Input label="Phone" value={f.phone} onChange={(v) => set('phone', v)} type="tel" required testid="report-phone" placeholder="+1 (416) 555-0123" />
                                <div className="grid grid-cols-2 gap-3">
                                    <Select label="Buying timeline" value={f.timeline} onChange={(v) => set('timeline', v)} options={TIMELINE_OPTIONS} testid="report-timeline" />
                                    <Input label="Budget (CAD)" value={f.budget} onChange={(v) => set('budget', v)} type="number" required testid="report-budget" placeholder="950000" />
                                </div>
                                <label className="block">
                                    <div className="overline mb-1.5">Area of interest</div>
                                    <select
                                        value={f.area}
                                        onChange={(e) => set('area', e.target.value)}
                                        required
                                        data-testid="report-area"
                                        className="w-full border border-neutral-300 px-3 py-2.5 text-[13.5px] focus:outline-none focus:border-black bg-white"
                                       
                                    >
                                        <option value="">Choose one…</option>
                                        {AREAS.map((a) => <option key={a} value={a}>{a.split('-').map(w => w[0].toUpperCase()+w.slice(1)).join(' ')}</option>)}
                                    </select>
                                </label>

                                <button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="report-submit"
                                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy text-white px-6 py-4 text-[14px] font-medium hover:bg-neutral-800 disabled:opacity-60"
                                    style={{ letterSpacing: '0.02em' }}
                                >
                                    {busy ? 'Preparing your PDF…' : <><Download className="w-4 h-4" /> Get the report</>}
                                </button>

                                <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-400 font-mono-data text-center pt-2">
                                    Free · No credit card · Unsubscribe anytime
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}


const Input = ({ label, value, onChange, type = 'text', required, testid, placeholder }) => (
    <label className="block">
        <div className="overline mb-1.5">{label}</div>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            placeholder={placeholder}
            data-testid={testid}
            className="w-full border border-neutral-300 px-3 py-2.5 text-[13.5px] focus:outline-none focus:border-black"
           
        />
    </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
    <label className="block">
        <div className="overline mb-1.5">{label}</div>
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="w-full border border-neutral-300 px-3 py-2.5 text-[13.5px] focus:outline-none focus:border-black bg-white"
           
        >
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    </label>
);
